import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supplierService } from '../../../services/supplierService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import MutateGate from '../../../components/Common/MutateGate';
import ViewOrEditLink from '../../../components/Common/ViewOrEditLink';

const VentasClients = () => {
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      loadClients();
    }
  }, []);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const response = await supplierService.getSuppliers({ entity_client: true });
      if (response.success && response.data) {
        setClients(response.data);
      } else {
        setToast({ isOpen: true, message: 'Error al cargar clientes', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar clientes',
        type: 'error',
      });
    } finally {
      setLoadingClients(false);
    }
  };

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    try {
      await supplierService.deleteSupplier(clientToDelete.id);
      setToast({ isOpen: true, message: 'Cliente eliminado', type: 'success' });
      loadClients();
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar cliente',
        type: 'error',
      });
    } finally {
      setIsDeleteModalOpen(false);
      setClientToDelete(null);
    }
  };

  const filtered = clients.filter((client) => {
    const docNumber = client.document_number || '';
    const matchesSearch =
      !searchTerm ||
      (client.code && client.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (client.name && client.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      docNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && client.is_active) ||
      (statusFilter === 'inactive' && !client.is_active);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Clientes</h1>
            <p className="text-gray-600 mt-1">Entidades registradas como clientes para ventas</p>
          </div>
          <MutateGate>
            <Link
              to="/ventas/clients/create"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nuevo Cliente
            </Link>
          </MutateGate>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
          <input
            type="text"
            placeholder="Buscar por código, nombre, documento o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {loadingClients ? (
          <div className="text-center py-12 text-gray-500">Cargando clientes…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md text-gray-500">No se encontraron clientes</div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filtered.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-mono text-teal-600">{client.code || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{client.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {client.document_type ? `${client.document_type.toUpperCase()}: ` : ''}
                      {client.document_number || '—'}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          client.is_active ? 'bg-teal-100 text-teal-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {client.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm space-x-3">
                      <ViewOrEditLink to={`/ventas/clients/${client.id}/edit`} />
                      <MutateGate>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(client)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Eliminar
                        </button>
                      </MutateGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="Eliminar cliente"
        message={`¿Eliminar al cliente "${clientToDelete?.name}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setClientToDelete(null);
        }}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default VentasClients;
